/**
 * ProjectMenu — menu de ações de um projeto (engrenagem → Editar / Excluir).
 *
 * Substitui os botões de exclusão diretos (lista e board) por um menu, reduzindo
 * cliques acidentais. Mesmo padrão visual do kebab das Pipelines (item = ícone +
 * título + descrição). `stopPropagation` no gatilho evita navegar ao abrir na
 * lista. `onEdit`/`onDelete` são os callbacks das ações.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../../components/icons.jsx'

export default function ProjectMenu({ onEdit, onDelete, onSaveTemplate, onStages, align = 'right', title = 'Ações do projeto', bare = false, portal = false }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const menuRef = useRef(null)
  const triggerRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!open || !portal) return
    const place = () => {
      const anchor = ref.current?.getBoundingClientRect()
      const menu = menuRef.current?.getBoundingClientRect()
      if (!anchor || !menu) return
      setPosition({
        left: Math.max(8, Math.min(align === 'right' ? anchor.right - menu.width : anchor.left, window.innerWidth - menu.width - 8)),
        top: anchor.bottom + menu.height + 6 <= window.innerHeight - 8 ? anchor.bottom + 6 : Math.max(8, anchor.top - menu.height - 6),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, portal, align])

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const stop = (e) => e.stopPropagation()

  const pick = (e, fn) => {
    e.stopPropagation()
    setOpen(false)
    fn?.()
  }

  const menu = open && (
        <div ref={menuRef} style={{ ...S.menu, ...(portal ? { position: 'fixed', marginTop: 0, zIndex: 1200, ...position } : align === 'right' ? { right: 0 } : { left: 0 }) }} role="menu" aria-label={title}>
          <button style={S.item} role="menuitem" onClick={(e) => pick(e, onEdit)}>
            <span style={S.icon}><Icon n="settings" s={15} /></span>
            <span>
              <span style={S.title}>Editar projeto</span>
              <span style={S.desc}>Nome, descrição, prioridade, status e prazo.</span>
            </span>
          </button>
          {onStages && <button type="button" style={S.item} role="menuitem" onClick={(e) => pick(e, onStages)}>
            <span style={S.icon}><Icon n="columns" s={15} /></span>
            <span><span style={S.title}>Configurar etapas</span><span style={S.desc}>Adicionar, renomear e organizar as etapas do projeto.</span></span>
          </button>}
          {onSaveTemplate && (
            <button style={S.item} role="menuitem" onClick={(e) => pick(e, onSaveTemplate)}>
              <span style={S.icon}><Icon n="download" s={15} /></span>
              <span>
                <span style={S.title}>Salvar como template</span>
                <span style={S.desc}>Reusar este quadro (colunas + tarefas) em novos projetos.</span>
              </span>
            </button>
          )}
          <button style={{ ...S.item, ...S.itemDanger }} role="menuitem" onClick={(e) => pick(e, onDelete)}>
            <span style={S.icon}><Icon n="x" s={15} /></span>
            <span>
              <span style={{ ...S.title, color: '#d92d20' }}>Excluir projeto</span>
              <span style={S.desc}>Remove o projeto e suas tarefas.</span>
            </span>
          </button>
        </div>
      )

  return (
    <div style={S.wrap} ref={ref} onClick={stop} onKeyDown={e => {
      if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); triggerRef.current?.focus() }
    }}>
      <button
        ref={triggerRef} type="button"
        className={bare ? 'pj-kebab' : undefined}
        style={bare ? undefined : { ...S.gear, ...(open ? S.gearActive : null) }}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
        title={title} aria-label={title} aria-haspopup="menu" aria-expanded={open}
      >
        <Icon n={bare ? 'more_v' : 'more'} s={bare ? 18 : 16} />
      </button>
      {portal ? createPortal(menu, document.body) : menu}
    </div>
  )
}

const S = {
  wrap: { position: 'relative', display: 'inline-flex' },
  gear: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid #d0d5dd', background: 'var(--surface)', color: '#667085',
    borderRadius: 8, padding: 5, cursor: 'pointer',
  },
  gearActive: { background: '#f9fafb', borderColor: '#2563eb', color: '#2563eb' },
  menu: {
    position: 'absolute', top: '100%', marginTop: 6, zIndex: 60, width: 250,
    background: 'var(--surface)', border: '1px solid #eaecf0', borderRadius: 12,
    boxShadow: '0 12px 32px rgba(16,24,40,.14)', padding: 6,
  },
  item: {
    display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', border: 'none',
    background: 'transparent', cursor: 'pointer', padding: '9px 10px', borderRadius: 8, textAlign: 'left', fontFamily: 'inherit',
  },
  itemDanger: {},
  icon: { color: '#667085', display: 'flex', marginTop: 1, flexShrink: 0 },
  title: { display: 'block', fontSize: 13, fontWeight: 700, color: '#101828' },
  desc: { display: 'block', fontSize: 11.5, color: '#667085', marginTop: 1, lineHeight: 1.35 },
}
